import React from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories whose changes propagate to the Centro de Comando / KPIs / Calendar.
// When an entry of this category is added/updated/deleted, we surface a subtle toast
// so the user *sees* the auto-sync happen.
const CASCADING_CATS = new Set([
  "kpis", "goals", "notes", "legacy", "roadmap", "command",
  "faith", "family", "emergency", "growth", "experiences", "hx", "shifts", "driving",
]);

const CASCADE_LABELS = {
  kpis:        "KPIs sincronizados",
  goals:       "Objetivos atualizados",
  notes:       "Lembretes & Calendário atualizados",
  legacy:      "Construtor de Legado atualizado",
  roadmap:     "Roadmap & Centro de Comando atualizados",
  command:     "Centro de Comando atualizado",
  faith:       "Fé & Dízimo · totais recalculados",
  family:      "Segurança Familiar · totais recalculados",
  emergency:   "Reserva de Emergência · recalculada",
  growth:      "Crescimento Pessoal · recalculado",
  experiences: "Experiências · recalculadas",
  hx:          "Investimentos HX · recalculados",
  shifts:      "Escala Semanal · totais recalculados",
  driving:     "Plano de Condução · atualizado",
};

/**
 * Centralised finance state. Loads all entries once on mount (when token present),
 * exposes CRUD helpers that keep local state in sync with MongoDB.
 *
 *  entriesByCat["notes"] -> list of entries for that category
 *  kpis["monthly_income"] -> entry object (category="kpis", label="monthly_income")
 */
export const useFinanceState = (token) => {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const headers = React.useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const refresh = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/finance/entries`, { headers });
      setEntries(data || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Subtle toast helper — fires once per (category, action) with throttling so a rapid
  // sequence of changes doesn't spam the UI.
  const lastToastRef = React.useRef({ at: 0, key: "" });
  const notifyCascade = React.useCallback((category, action) => {
    const msg = CASCADE_LABELS[category];
    if (!msg || !CASCADING_CATS.has(category)) return;
    const now = Date.now();
    const key = `${category}:${action}`;
    // Throttle: skip if same key within 800ms (rapid edits)
    if (now - lastToastRef.current.at < 800 && lastToastRef.current.key === key) return;
    lastToastRef.current = { at: now, key };
    toast.success(`→ ${msg}`, { duration: 1500, position: "bottom-right" });
  }, []);

  const upsert = React.useCallback(async (payload) => {
    if (!token) return null;
    const { data } = await axios.post(`${API}/finance/upsert`, payload, { headers });
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.category === data.category && e.label === data.label);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = data; return next;
      }
      return [data, ...prev];
    });
    notifyCascade(data.category, "upsert");
    return data;
  }, [token, headers, notifyCascade]);

  const create = React.useCallback(async (payload) => {
    if (!token) return null;
    const { data } = await axios.post(`${API}/finance/entries`, payload, { headers });
    setEntries((prev) => [data, ...prev]);
    notifyCascade(data.category, "create");
    return data;
  }, [token, headers, notifyCascade]);

  const update = React.useCallback(async (id, payload) => {
    if (!token) return null;
    const { data } = await axios.patch(`${API}/finance/entries/${id}`, payload, { headers });
    setEntries((prev) => prev.map((e) => e.id === id ? data : e));
    notifyCascade(data.category, "update");
    return data;
  }, [token, headers, notifyCascade]);

  const remove = React.useCallback(async (id) => {
    if (!token) return;
    const target = entries.find((e) => e.id === id);
    await axios.delete(`${API}/finance/entries/${id}`, { headers });
    setEntries((prev) => prev.filter((e) => e.id !== id));
    if (target) notifyCascade(target.category, "delete");
  }, [token, headers, entries, notifyCascade]);

  // Indexed views
  const entriesByCat = React.useMemo(() => {
    const m = {};
    for (const e of entries) {
      if (!m[e.category]) m[e.category] = [];
      m[e.category].push(e);
    }
    return m;
  }, [entries]);

  const kpis = React.useMemo(() => {
    const m = {};
    for (const e of (entriesByCat.kpis || [])) {
      m[e.label] = e;
    }
    return m;
  }, [entriesByCat]);

  return { entries, entriesByCat, kpis, loading, refresh, upsert, create, update, remove };
};
